import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { Box, Stack } from '@mui/material';

import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import type { DonutOptionSelection } from './DonutInsightCard';
import { InsightGroupSection } from './InsightGroupSection';
import { StatTile } from './StatTile';
import { hasInsightData } from './insightMath';
import type { AnalyticsDashboardData } from './types';
import type { AnalyticsDashboardState } from './useAnalyticsDashboard';

export type AnalyticsDashboardViewProps = {
  state: AnalyticsDashboardState;
  onRetry?: () => void;
  onOptionSelect?: (selection: DonutOptionSelection) => void;
};

// An insight object with no options/items (structurally valid, but empty)
// doesn't count as data — only a real, chartable insight does (SFOA-35 AC #5).
const hasAnyInsight = (data: AnalyticsDashboardData): boolean =>
  data.groups.some(group => group.insights.some(hasInsightData));

export const AnalyticsDashboardView = ({ state, onRetry, onOptionSelect }: AnalyticsDashboardViewProps) => {
  return (
    <Box component="section" aria-label="Analytics results" aria-busy={state.status === 'loading'}>
      {state.status === 'loading' ? (
        <LoadingState message="Loading analytics…" />
      ) : state.status === 'error' ? (
        <ErrorState message={state.message} onRetry={onRetry} />
      ) : (
        <Stack sx={{ gap: 5 }}>
          {state.data.stats.length > 0 && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
              {state.data.stats.map(stat => (
                <StatTile key={stat.key} stat={stat} />
              ))}
            </Box>
          )}
          {hasAnyInsight(state.data) ? (
            state.data.groups
              .filter(group => group.insights.some(hasInsightData))
              .map(group => <InsightGroupSection key={group.key} group={group} onOptionSelect={onOptionSelect} />)
          ) : (
            <EmptyState
              Icon={InsightsOutlinedIcon}
              title="No insights yet"
              hint="Aggregated voter insights will appear here as canvassing conversations are completed and analyzed."
            />
          )}
        </Stack>
      )}
    </Box>
  );
};
